export type Bi = { fr: string; en: string }

export const BROSSARD_IMAGES = {
  panorama: 'https://cdn.magicpatterns.com/patterns/generated-images/bdda70cb-8769-4c08-ac45-22321186e1d1.jpg',
  counter: 'https://cdn.magicpatterns.com/patterns/generated-images/5b559743-f716-4485-bb21-535a55a1f7e9.jpg',
  cardPark: 'https://cdn.magicpatterns.com/patterns/generated-images/fa40770c-f886-4551-b595-051f4c1aa207.jpg',
}

/* ---------- Section 1 — Proof ---------- */
export const proofCopy = {
  eyebrow: { fr: 'Étude de cas', en: 'Case study' },
  eyebrowCity: 'Brossard',
  headline: [
    { text: { fr: 'Une ville connectée', en: 'A connected city' }, accent: false },
    { text: { fr: 'commence par ses citoyens.', en: 'starts with its residents.' }, accent: true },
  ],
  bodyStart: {
    fr: 'Depuis 2017, Brossard s’appuie sur BCITI | ATNA pour relier identité, communications et services dans une même expérience numérique — pour ses ',
    en: 'Since 2017, Brossard has relied on BCITI | ATNA to connect identity, communications and services in one digital experience — for its ',
  },
  bodyStrong: { fr: 'résidents et ses non-résidents.', en: 'residents and non-residents.' },
  cta: { fr: 'Voir ce que nous avons connecté', en: 'See what we connected' },
}

export const proofMetrics: { key: 'identities' | 'coverage' | 'since'; value: Bi; label: Bi; note?: Bi }[] = [
  {
    key: 'identities',
    value: { fr: '88 266', en: '88,266' },
    label: { fr: 'usagers actifs', en: 'active users' },
    note: { fr: 'Résidents et non-résidents', en: 'Residents and non-residents' },
  },
  {
    key: 'coverage',
    value: { fr: '78 %', en: '78%' },
    label: { fr: 'de la population couverte', en: 'of the population covered' },
    note: { fr: 'Résidents et non-résidents', en: 'Residents and non-residents' },
  },
  {
    key: 'since',
    value: { fr: 'Depuis 2017', en: 'Since 2017' },
    label: { fr: 'en production', en: 'in production' },
  },
]

/* Device mock copy — illustrative interface content */
export const phoneCopy = {
  card: { fr: 'Carte numérique', en: 'Digital Card' },
  name: 'Marie Tremblay',
  role: { fr: 'Citoyenne', en: 'Resident' },
  services: { fr: 'Mes services', en: 'My services' },
  tiles: [
    { key: 'collect', label: { fr: 'Collectes', en: 'Waste collection' } },
    { key: 'requests', label: { fr: 'Requêtes', en: 'Requests' } },
    { key: 'permits', label: { fr: 'Permis', en: 'Permits' } },
    { key: 'payments', label: { fr: 'Paiements', en: 'Payments' } },
    { key: 'comms', label: { fr: 'Communications', en: 'Communications' } },
    { key: 'more', label: { fr: 'Plus de services', en: 'More services' } },
  ] as { key: 'collect' | 'requests' | 'permits' | 'payments' | 'comms' | 'more'; label: Bi }[],
  tabs: [
    { fr: 'Accueil', en: 'Home' },
    { fr: 'Services', en: 'Services' },
    { fr: 'Carte', en: 'Card' },
    { fr: 'Messages', en: 'Messages' },
    { fr: 'Profil', en: 'Profile' },
  ],
}

export const dashCopy = {
  title: { fr: 'Vue d’ensemble', en: 'Overview' },
  nav: [
    { fr: 'Tableau de bord', en: 'Dashboard' },
    { fr: 'Citoyens', en: 'Residents' },
    { fr: 'Requêtes', en: 'Requests' },
    { fr: 'Collectes', en: 'Waste collection' },
    { fr: 'Permis', en: 'Permits' },
    { fr: 'Communications', en: 'Communications' },
    { fr: 'Paiements', en: 'Payments' },
    { fr: 'Analytique', en: 'Analytics' },
  ],
  kpis: [
    { value: { fr: '88 266', en: '88,266' }, label: { fr: 'Identités vérifiées', en: 'Verified identities' } },
    { value: { fr: '78 %', en: '78%' }, label: { fr: 'Couverture de la population', en: 'Population coverage' } },
    { value: { fr: '2 341', en: '2,341' }, label: { fr: 'Requêtes ce mois-ci', en: 'Requests this month' } },
  ],
  activity: { fr: 'Activité des services', en: 'Service activity' },
  months: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin'],
  monthsEn: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
  top: { fr: 'Services les plus utilisés', en: 'Most used services' },
  topRows: [
    { label: { fr: 'Collectes', en: 'Waste collection' }, value: { fr: '42 %', en: '42%' } },
    { label: { fr: 'Requêtes', en: 'Requests' }, value: { fr: '28 %', en: '28%' } },
    { label: { fr: 'Permis', en: 'Permits' }, value: { fr: '18 %', en: '18%' } },
    { label: { fr: 'Paiements', en: 'Payments' }, value: { fr: '12 %', en: '12%' } },
  ],
  demoNote: 'Illustrative interface data (demo) — only 88 266 and 78 % are published Brossard figures.',
}

/* ---------- Section 2 — Transformation ---------- */
export const transformCopy = {
  eyebrow: { fr: 'Une fondation qui change l’expérience', en: 'A foundation that changes the experience' },
  headline: [
    { text: { fr: 'De services dispersés', en: 'From scattered services' }, accent: false },
    { text: { fr: 'à un écosystème connecté.', en: 'to a connected ecosystem.' }, accent: true },
  ],
  before: {
    title: { fr: 'Avant', en: 'Before' },
    items: [
      { key: 'separate', label: { fr: 'Services séparés', en: 'Separate services' } },
      {
        key: 'identities',
        label: { fr: 'Identités dispersées', en: 'Scattered identities' },
        note: { fr: '(résidents et non-résidents)', en: '(residents and non-residents)' },
      },
      { key: 'fragmented', label: { fr: 'Interactions fragmentées', en: 'Fragmented interactions' } },
      { key: 'manual', label: { fr: 'Traitements manuels', en: 'Manual processing' } },
    ] as { key: 'separate' | 'identities' | 'fragmented' | 'manual'; label: Bi; note?: Bi }[],
  },
  core: {
    title: 'BCITI | ATNA',
    identity: [
      { fr: 'Identité', en: 'Verified' },
      { fr: 'vérifiée', en: 'identity' },
    ],
    who: { fr: 'Résidents · non-résidents · organisations', en: 'Residents · non-residents · organizations' },
    services: [
      { key: 'card', label: { fr: 'Carte numérique', en: 'Digital Card' } },
      { key: 'comms', label: { fr: 'Communications', en: 'Communications' } },
      { key: 'collect', label: { fr: 'Collectes', en: 'Waste collection' } },
      { key: 'requests', label: { fr: 'Requêtes', en: 'Requests' } },
      { key: 'payments', label: { fr: 'Paiements', en: 'Payments' } },
      { key: 'municipal', label: { fr: 'Services municipaux', en: 'Municipal services' } },
    ] as { key: 'card' | 'comms' | 'collect' | 'requests' | 'payments' | 'municipal'; label: Bi }[],
  },
  today: {
    title: { fr: 'Aujourd’hui', en: 'Today' },
    items: [
      {
        key: 'identities',
        value: { fr: '88 266', en: '88,266' },
        label: { fr: 'usagers actifs', en: 'active users' },
        note: { fr: '(résidents et non-résidents)', en: '(residents and non-residents)' },
      },
      {
        key: 'coverage',
        value: { fr: '78 %', en: '78%' },
        label: { fr: 'de couverture', en: 'coverage' },
        note: { fr: '(résidents et non-résidents)', en: '(residents and non-residents)' },
      },
      {
        key: 'relation',
        value: { fr: 'Une relation', en: 'A continuous' },
        label: { fr: 'numérique continue', en: 'digital relationship' },
        note: { fr: 'avant, pendant et après', en: 'before, during and after' },
      },
    ] as { key: 'identities' | 'coverage' | 'relation'; value: Bi; label: Bi; note: Bi }[],
  },
  impact: {
    from: '400',
    to: '5',
    text: {
      fr: 'Personnes en attente au comptoir pour un renouvellement de carte numérique — file presque éliminée.',
      en: 'People waiting at the counter for a digital card renewal — queue almost eliminated.',
    },
  },
  statement: {
    line1: { fr: 'L’identité ne fait pas que connecter la Ville à ses usagers.', en: 'Identity doesn’t just connect the City to its users.' },
    before: { fr: 'Elle tisse ', en: 'It builds ' },
    accent: { fr: 'un lien de confiance durable', en: 'a lasting bond of trust' },
    after: {
      fr: ' avec chaque résident, non-résident et organisation qui interagit avec ses services.',
      en: ' with every resident, non-resident and organization that interacts with its services.',
    },
    link: { fr: 'Découvrir la plateforme', en: 'Discover the platform' },
  },
}

/* ---------- Section 3 — Promise ---------- */
export const promiseCopy = {
  eyebrow: { fr: 'De Brossard à votre organisation', en: 'From Brossard to your organization' },
  headline: [
    { text: { fr: 'Une fondation éprouvée.', en: 'A proven foundation.' }, accent: false },
    { text: { fr: 'Désormais enrichie', en: 'Now augmented' }, accent: true },
    { text: { fr: 'par l’intelligence.', en: 'by intelligence.' }, accent: true },
  ],
  body: {
    fr: 'BCITI | ATNA transforme une identité numérique en contexte opérationnel : comprendre qui demande quoi, relier les bons services et données, vérifier l’information et orchestrer les prochaines actions.',
    en: 'BCITI | ATNA turns a digital identity into operational context: understanding who is asking for what, connecting the right services and data, verifying information and orchestrating the next actions.',
  },
  steps: [
    {
      key: 'identity',
      title: [
        { fr: 'Identité', en: 'Identity' },
        { fr: 'et contexte', en: '& context' },
      ],
      text: { fr: 'Qui est cette personne ? Quel est son contexte ?', en: 'Who is this person? What is their context?' },
    },
    {
      key: 'services',
      title: [
        { fr: 'Services', en: 'Connected' },
        { fr: 'connectés', en: 'services' },
      ],
      text: { fr: 'Relier les bons services et données', en: 'Connect the right services and data' },
    },
    {
      key: 'atna',
      title: [
        { fr: 'Intelligence', en: 'ATNA' },
        { fr: 'ATNA', en: 'intelligence' },
      ],
      text: { fr: 'Comprendre · vérifier · recommander', en: 'Understand · verify · recommend' },
    },
    {
      key: 'actions',
      title: [
        { fr: 'Recommandations', en: 'Verified' },
        { fr: 'vérifiées et actions', en: 'recommendations + actions' },
      ],
      text: { fr: 'Orchestrer les prochaines actions', en: 'Orchestrate the next actions' },
    },
  ] as { key: 'identity' | 'services' | 'atna' | 'actions'; title: Bi[]; text: Bi }[],
  cta: {
    line1: { fr: 'Et si votre organisation pouvait', en: 'What if your organization could' },
    line2: { fr: 'réunir toute sa relation ', en: 'bring together its entire relationship ' },
    accent: { fr: 'avec ses usagers ?', en: 'with its users?' },
    primary: { fr: 'Demander une démo', en: 'Request a demo' },
    secondary: { fr: 'Explorer la plateforme', en: 'Explore the platform' },
  },
}
